import type { BusinessReport, BusinessReportOutcome, TemplateCategory } from "./businessReports.types";
import { getUncategorisedLabel } from "./templateLibrary";

export interface TemplateWorkspaceSummary {
  active: number;
  authoringTemplates: number;
  drafts: number;
  reportTemplates: number;
  snapshotTemplates: number;
}

export interface OutcomeWorkspaceSummary {
  failed: number;
  processing: number;
  successful: number;
  total: number;
}

const processingStatuses = new Set<BusinessReportOutcome["status"]>(["IN_PROGRESS", "NEW"]);

export const getTemplateWorkspaceSummary = (templates: readonly BusinessReport[]): TemplateWorkspaceSummary => ({
  active: templates.filter(template => template.is_active !== false).length,
  authoringTemplates: templates.filter(
    template => template.type === "authoring_template" && template.is_active !== false,
  ).length,
  drafts: templates.filter(template => template.is_active === false).length,
  reportTemplates: templates.filter(template => template.type === "business_report" && template.is_active !== false)
    .length,
  snapshotTemplates: templates.filter(
    template =>
      template.type !== "business_report" && template.type !== "authoring_template" && template.is_active !== false,
  ).length,
});

export const getOutcomeWorkspaceSummary = (
  outcomes: readonly Pick<BusinessReportOutcome, "status">[],
  total = outcomes.length,
): OutcomeWorkspaceSummary => ({
  failed: outcomes.filter(outcome => outcome.status === "FAILED").length,
  processing: outcomes.filter(outcome => processingStatuses.has(outcome.status)).length,
  successful: outcomes.filter(outcome => outcome.status === "COMPLETED").length,
  total,
});

export const getTemplateCategoryCounts = (
  templates: readonly BusinessReport[],
  categories: readonly TemplateCategory[] = [],
): Array<{ categoryId: string | null; label: string; value: number }> => {
  const counts = new Map<string, { categoryId: string | null; label: string; value: number }>();

  for (const category of categories) {
    counts.set(category.id, { categoryId: category.id, label: category.name, value: 0 });
  }

  for (const template of templates) {
    const categoryId = template.category?.id ?? null;
    const label = template.category?.name ?? getUncategorisedLabel(template.type);
    const key = categoryId ?? label;
    const count = counts.get(key);
    counts.set(key, { categoryId, label, value: (count?.value ?? 0) + 1 });
  }

  return Array.from(counts.values()).sort((first, second) => first.label.localeCompare(second.label));
};
